import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiClient } from '../../services/apiClient';
import './CallbackRequest.css';

export default function CallbackRequest() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    preferredTime: ''
  });

  const [submitStatus, setSubmitStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('Submitting...');

    try {
      // Send callback request to backend
      const response = await apiClient.post('/callback-requests', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        preferredTime: formData.preferredTime,
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      if (response && response.success) {
        setSubmitStatus('Callback request submitted successfully! We will contact you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          preferredTime: ''
        });
      } else {
        setSubmitStatus('Failed to submit callback request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setSubmitStatus('Error submitting callback request. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="callback-request-page">
      {/* Hero Section */}
      <section className="callback-hero">
        <div className="callback-hero__inner">
          <h1>Request a Callback</h1>
          <p>Fill out the form below and our team will get back to you at your preferred time</p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="callback-breadcrumb">
        <div className="breadcrumb-content">
          <NavLink to="/">HOME</NavLink>
          <span aria-hidden>›</span>
          <strong>Request Callback</strong>
        </div>
      </div>

      {/* Callback Form */}
      <section className="callback-form-section">
        <motion.div 
          className="callback-form"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="form-header">
            <h2>We'll Call You Back</h2>
            <p>Provide your details and we'll contact you as soon as possible</p>
          </div>

          {submitStatus && (
            <div className={`status-message ${submitStatus.includes('successfully') ? 'success' : 'error'}`}>
              {submitStatus}
            </div>
          )}

          <form onSubmit={handleSubmit} className="callback-form__form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Enter your phone number"
                />
              </div>

              <div className="form-group">
                <label htmlFor="preferredTime">Preferred Time</label>
                <select
                  id="preferredTime"
                  name="preferredTime"
                  value={formData.preferredTime}
                  onChange={handleChange}
                >
                  <option value="">Select preferred time</option>
                  <option value="9-10">9:00 AM - 10:00 AM</option>
                  <option value="10-11">10:00 AM - 11:00 AM</option>
                  <option value="11-12">11:00 AM - 12:00 PM</option>
                  <option value="12-1">12:00 PM - 1:00 PM</option>
                  <option value="1-2">1:00 PM - 2:00 PM</option>
                  <option value="2-3">2:00 PM - 3:00 PM</option>
                  <option value="3-4">3:00 PM - 4:00 PM</option>
                  <option value="4-5">4:00 PM - 5:00 PM</option>
                  <option value="5-6">5:00 PM - 6:00 PM</option>
                  <option value="6-7">6:00 PM - 7:00 PM</option>
                </select>
              </div>
            </div>

            <div className="form-group full-width">
              <label htmlFor="message">Message (Optional)</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us what you need help with..."
                rows="4"
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Callback'}
            </button>
          </form>
        </motion.div>
      </section>
    </main>
  );
}
